import React, { useState, useMemo, useEffect, createElement, useCallback } from "react";
import { Row, Col, Card } from "antd";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { animated, to } from '@react-spring/web';
import { useTooltip } from '@nivo/tooltip';
import SummaryCard from "../Analytics/SummaryCard";
import SummaryGrid from "../Analytics/SummaryGrid";
import { primaryShades, secondaryShades, variantTokens, greyScaleTokens } from "../../theme/theme";
import EmptyCard from "../EmptyCard";
import { formatNumberBritish } from "../../utils/numberFormatting";
import LoadingCard from "../Components/Cards/LoadingCard";

// Costanti dei colori dal tema
const PRIMARY = variantTokens.primary;
const PRIMARY_300 = primaryShades.primary300;
const PRIMARY_200 = primaryShades.primary200;

const SECONDARY = variantTokens.secondary;
const SECONDARY_200 = secondaryShades.secondary200;
const SECONDARY_100 = secondaryShades.secondary100;

const ACCENT = variantTokens.accent;

// Palette per i gradienti - utilizziamo le costanti del tema
const primaryLighter = PRIMARY_300;     // Versione più chiara del primary
const primaryLightest = PRIMARY_200;    // Versione ancora più chiara
const primaryVeryLight = greyScaleTokens.grey100; // Usato per sfumature quasi bianche
const secondaryLighter = SECONDARY_200; // Versione più chiara del secondary
const secondaryLightest = SECONDARY_100; // Versione ancora più chiara
const accentLighter = '#DAB4CD';       // Versione più chiara dell'accent
const accentLightest = '#E9D4E2';      // Versione ancora più chiara

// Custom bar component with only top corners rounded
const TopRoundedBar = (props) => {
    const {
        bar: { data, ...bar },
        style: {
            borderColor,
            color,
            height,
            labelColor,
            labelOpacity,
            labelX,
            labelY,
            transform,
            width,
        },
        borderRadius,
        borderWidth,
        label,
        shouldRenderLabel,
        isInteractive,
        onClick,
        onMouseEnter,
        onMouseLeave,
        tooltip,
        isFocusable,
        ariaLabel,
        ariaLabelledBy,
        ariaDescribedBy,
    } = props;

    const { theme } = props;
    const { showTooltipFromEvent, showTooltipAt, hideTooltip } = useTooltip();

    const renderTooltip = useMemo(
        () => () => createElement(tooltip, { ...bar, ...data }),
        [tooltip, bar, data]
    );
    const handleClick = useCallback(
        (event) => onClick && onClick({ color: bar.color, ...data }, event),
        [bar, data, onClick]
    );
    const handleTooltip = useCallback(
        (event) => showTooltipFromEvent(renderTooltip(), event),
        [showTooltipFromEvent, renderTooltip]
    );
    const handleMouseEnter = useCallback(
        (event) => {
            onMouseEnter && onMouseEnter(data, event);
            showTooltipFromEvent(renderTooltip(), event);
        },
        [data, onMouseEnter, showTooltipFromEvent, renderTooltip]
    );
    const handleMouseLeave = useCallback(
        (event) => {
            onMouseLeave && onMouseLeave(data, event);
            hideTooltip();
        },
        [data, hideTooltip, onMouseLeave]
    );
    const handleFocus = useCallback(
        () =>
            showTooltipAt(renderTooltip(), [
                bar.absX + bar.width / 2,
                bar.absY,
            ]),
        [showTooltipAt, renderTooltip, bar]
    );
    const handleBlur = useCallback(() => hideTooltip(), [hideTooltip]);

    return (
        <animated.g transform={transform}>
            <defs>
                <clipPath id={`round-corner-${label}`}>
                    <animated.rect
                        x={0}
                        y={0}
                        rx={borderRadius}
                        ry={borderRadius}
                        width={to(width, (v) => Math.max(v, 0))}
                        height={to(height, (v) => Math.max(v + borderRadius, 0))}
                    />
                </clipPath>
            </defs>
            <animated.rect
                clipPath={`url(#round-corner-${label})`}
                width={to(width, (v) => Math.max(v, 0))}
                height={to(height, (v) => Math.max(v, 0))}
                fill={data.fill ?? color}
                strokeWidth={borderWidth}
                stroke={borderColor}
                focusable={isFocusable}
                tabIndex={isFocusable ? 0 : undefined}
                aria-label={ariaLabel ? ariaLabel(data) : undefined}
                aria-labelledby={ariaLabelledBy ? ariaLabelledBy(data) : undefined}
                aria-describedby={ariaDescribedBy ? ariaDescribedBy(data) : undefined}
                onMouseEnter={
                    isInteractive ? handleMouseEnter : undefined
                }
                onMouseMove={isInteractive ? handleTooltip : undefined}
                onMouseLeave={
                    isInteractive ? handleMouseLeave : undefined
                }
                onClick={isInteractive ? handleClick : undefined}
                onFocus={
                    isInteractive && isFocusable ? handleFocus : undefined
                }
                onBlur={
                    isInteractive && isFocusable ? handleBlur : undefined
                }
            />
            {shouldRenderLabel && (
                <animated.text
                    x={labelX}
                    y={labelY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fillOpacity={labelOpacity}
                    style={{
                        ...theme.labels.text,
                        pointerEvents: 'none',
                        fill: labelColor,
                    }}
                >
                    {label}
                </animated.text>
            )}
        </animated.g>
    );
};


function DeviceAnalytics({ data = [], loading }) {
    const [selectedTab, setSelectedTab] = useState("All Devices");
    const [filteredColumnData, setFilteredColumnData] = useState([]);

    // Tooltip per le SummaryCard aggiornati con descrizioni più complete e informative
    const tooltipTextMap = {
        "All Devices": "Distribution of all devices by operating system and version across all sessions",
        "Mobile Devices": "Distribution of mobile devices including smartphones and tablets by operating system",
        "Desktop Devices": "Distribution of desktop computers by operating system and browser"
    };

    // Usa useMemo per elaborare i dati solo quando data o selectedTab cambiano
    const { columnData, pieData, deviceSummary } = useMemo(() => {
        if (!data || data.length === 0) {
            return {
                columnData: [],
                pieData: [],
                deviceSummary: {
                    allDevices: 0,
                    mobileDevices: 0,
                    desktopDevices: 0
                }
            };
        }

        const browserCounts = {};
        const deviceTypeCounts = { mobile: 0, desktop: 0, tablet: 0 };

        data.forEach(({ device_os }) => {
            const browser = device_os?.split(" - ")[0]?.trim() || "Unknown";
            let deviceType = device_os?.split(" - ")[1]?.toLowerCase() || "unknown";

            // Conta i browser
            if (!browserCounts[browser]) {
                browserCounts[browser] = 0;
            }
            browserCounts[browser]++;

            // Assegna "mobile" come default se non è desktop, tablet o mobile
            if (deviceType.includes("desktop")) {
                deviceTypeCounts.desktop++;
            } else if (deviceType.includes("tablet")) {
                deviceTypeCounts.tablet++;
            } else {
                deviceTypeCounts.mobile++; // Default a "mobile"
            }
        });

        // Prepara i dati per il grafico a barre
        let processedColumnData = Object.entries(browserCounts)
            .map(([name, count]) => ({ type: name, value: count }))
            .sort((a, b) => b.value - a.value);

        // Limita il numero di entry per evitare sovraffollamento nel grafico
        if (processedColumnData.length > 8) {
            processedColumnData = processedColumnData.slice(0, 8);
        }

        // Prepara i dati per il grafico a torta
        const processedPieData = [
            { id: "Desktop", value: deviceTypeCounts.desktop },
            { id: "Mobile", value: deviceTypeCounts.mobile },
            { id: "Tablet", value: deviceTypeCounts.tablet }
        ].filter(item => item.value > 0); // Rimuovi item con valore 0

        // Calcola il sommario dei dispositivi
        const summary = {
            allDevices: deviceTypeCounts.mobile + deviceTypeCounts.desktop + deviceTypeCounts.tablet,
            mobileDevices: deviceTypeCounts.mobile,
            desktopDevices: deviceTypeCounts.desktop,
        };

        return {
            columnData: processedColumnData,
            pieData: processedPieData,
            deviceSummary: summary
        };
    }, [data]);

    // Effetto per filtrare i dati delle colonne in base alla scheda selezionata
    useEffect(() => {
        if (!data || data.length === 0 || !columnData || columnData.length === 0) {
            setFilteredColumnData([]);
            return;
        }

        let total = 0;

        if (selectedTab === "Mobile Devices") {
            // Filtra solo i dispositivi mobili
            const mobileBrowsers = {};
            data.forEach(({ device_os }) => {
                const browser = device_os?.split(" - ")[0]?.trim() || "Unknown";
                let deviceType = device_os?.split(" - ")[1]?.toLowerCase() || "unknown";

                // Usa la stessa logica di classificazione del useMemo principale
                if (!deviceType.includes("desktop") && !deviceType.includes("tablet")) {
                    if (!mobileBrowsers[browser]) {
                        mobileBrowsers[browser] = 0;
                    }
                    mobileBrowsers[browser]++;
                }
            });

            let filtered = Object.entries(mobileBrowsers)
                .map(([type, value]) => ({ type, value }))
                .sort((a, b) => b.value - a.value);

            if (filtered.length > 8) {
                filtered = filtered.slice(0, 8);
            }

            total = filtered.reduce((sum, item) => sum + item.value, 0);

            setFilteredColumnData(filtered);
        } else if (selectedTab === "Desktop Devices") {
            // Filtra solo i dispositivi desktop
            const desktopBrowsers = {};
            data.forEach(({ device_os }) => {
                const browser = device_os?.split(" - ")[0]?.trim() || "Unknown";
                let deviceType = device_os?.split(" - ")[1]?.toLowerCase() || "unknown";

                // Usa la stessa logica di classificazione del useMemo principale
                if (deviceType.includes("desktop")) {
                    if (!desktopBrowsers[browser]) {
                        desktopBrowsers[browser] = 0;
                    }
                    desktopBrowsers[browser]++;
                }
            });

            let filtered = Object.entries(desktopBrowsers)
                .map(([type, value]) => ({ type, value }))
                .sort((a, b) => b.value - a.value);

            if (filtered.length > 8) {
                filtered = filtered.slice(0, 8);
            }

            total = filtered.reduce((sum, item) => sum + item.value, 0);

            setFilteredColumnData(filtered);
        } else {
            // Tutti i dispositivi (usa i dati originali)
            total = columnData.reduce((sum, item) => sum + item.value, 0);

            setFilteredColumnData(columnData);
        }
    }, [data, columnData, selectedTab, deviceSummary]);

    const handleTabChange = (tab) => {
        setSelectedTab(tab);
    };

    // Calcola il totale dei dispositivi per il centro del grafico
    const deviceTotal = useMemo(() => {
        if (!pieData || pieData.length === 0) return 0;
        return pieData.reduce((sum, item) => sum + item.value, 0);
    }, [pieData]);

    // Definizione dei gradienti per gli spicchi del grafico
    const defs = [
        {
            id: 'desktopGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: PRIMARY },
                { offset: 40, color: `${primaryLighter}DD` },
                { offset: 80, color: `${primaryLightest}CC` },
                { offset: 100, color: `${primaryVeryLight}BB` },
            ],
        },
        {
            id: 'mobileGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: SECONDARY },
                { offset: 40, color: `${secondaryLighter}DD` },
                { offset: 80, color: `${secondaryLightest}CC` },
                { offset: 100, color: `${secondaryLightest}BB` },
            ],
        },
        {
            id: 'tabletGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: ACCENT },
                { offset: 40, color: `${accentLighter}DD` },
                { offset: 80, color: `${accentLighter}CC` },
                { offset: 100, color: `${accentLightest}BB` },
            ],
        },
        {
            id: 'barGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: PRIMARY },
                { offset: 40, color: `${primaryLighter}DD` },
                { offset: 80, color: `${primaryLightest}CC` },
                { offset: 100, color: `${primaryVeryLight}BB` },
            ],
        },
    ];

    // Mappatura dei colori per gli spicchi
    const fill = [
        { match: { id: 'Desktop' }, id: 'desktopGradient' },
        { match: { id: 'Mobile' }, id: 'mobileGradient' },
        { match: { id: 'Tablet' }, id: 'tabletGradient' },
    ];

    // Mappatura dei colori per le barre
    const barFill = [
        { match: '*', id: 'barGradient' }
    ];

    // Stile del tooltip personalizzato
    const tooltipStyle = {
        backgroundColor: 'white',
        padding: '8px 12px',
        border: '1px solid #e8e8e8',
        borderRadius: '2px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    };

    // Aggiungi un controllo per verificare se ci sono dati
    const hasData = useMemo(() => {
        return columnData && columnData.length > 0;
    }, [columnData]);

    return (
        <>
            <SummaryGrid>
                {[
                    { key: "All Devices", value: deviceSummary.allDevices },
                    { key: "Mobile Devices", value: deviceSummary.mobileDevices },
                    { key: "Desktop Devices", value: deviceSummary.desktopDevices },
                ].map(({ key, value }, index) => {
                    // Calcola la percentuale per Mobile e Desktop Devices
                    let percentage = null;
                    if (key === "Mobile Devices" && deviceSummary.allDevices > 0) {
                        percentage = ((deviceSummary.mobileDevices / deviceSummary.allDevices) * 100).toFixed(1);
                    } else if (key === "Desktop Devices" && deviceSummary.allDevices > 0) {
                        percentage = ((deviceSummary.desktopDevices / deviceSummary.allDevices) * 100).toFixed(1);
                    }

                    return (
                        <SummaryCard
                            key={key}
                            label={key}
                            value={value !== undefined ? value : 0}
                            percentage={percentage}
                            loading={loading}
                            active={key === selectedTab}
                            onClick={() => handleTabChange(key)}
                            toolTipText={tooltipTextMap[key]}
                            index={index}
                            totalitems={3}
                        />
                    );
                })}
            </SummaryGrid>

            <Row gutter={[16, 16]} justify="space-between">
                {loading ? (
                    <Col span={24}>
                        <LoadingCard />
                    </Col>
                ) : !hasData ? (
                    <Col span={24}>
                        <EmptyCard />
                    </Col>
                ) : (
                    <>
                        <Col xs={24} md={16}>
                            <Card
                                styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
                                style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
                            >
                                <div style={{ height: '100%' }}>
                                    <ResponsiveBar
                                        data={filteredColumnData}
                                        keys={['value']}
                                        indexBy="type"
                                        margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                                        padding={0.7}
                                        innerPadding={4}
                                        minValue={0}
                                        maxValue={filteredColumnData.length > 0 ? Math.max(...filteredColumnData.map(d => d.value)) * 1.2 : 0}
                                        groupMode="grouped"
                                        layout="vertical"
                                        valueScale={{ type: 'linear' }}
                                        indexScale={{
                                            type: 'band',
                                            round: true,
                                            padding: 0.5
                                        }}
                                        maxBarSize={20}
                                        enableGridY={true}
                                        gridYValues={3}
                                        borderRadius={8}
                                        barComponent={TopRoundedBar}
                                        borderWidth={1}
                                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                        defs={defs}
                                        fill={barFill}
                                        colors={[PRIMARY]}
                                        enableLabel={false}
                                        axisBottom={{
                                            tickSize: 5, tickPadding: 5, tickRotation: 0,
                                            legend: '', legendPosition: 'middle', legendOffset: 32
                                        }}
                                        axisLeft={{
                                            tickSize: 5, tickPadding: 5, tickRotation: 0,
                                            legend: '', legendPosition: 'middle', legendOffset: -40,
                                            tickValues: 3,
                                            format: (value) => Math.round(value)
                                        }}
                                        tooltip={({ data, value, color, indexValue }) => {
                                            const total = filteredColumnData.reduce((sum, item) => sum + item.value, 0);
                                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                            return (
                                                <div style={tooltipStyle}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <div style={{
                                                            width: '12px',
                                                            height: '12px',
                                                            backgroundColor: color,
                                                            marginRight: '8px'
                                                        }} />
                                                        <span style={{ fontWeight: 'bold' }}>{indexValue}:</span>
                                                        <span style={{ marginLeft: '4px' }}>
                                                            {formatNumberBritish(value, 0)} ({percentage}%)
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                        motionConfig="gentle"
                                        animate={true}
                                    />
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} md={8}>
                            <Card
                                styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
                                style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
                            >
                                <div style={{ height: '100%', position: 'relative' }}>
                                    <ResponsivePie
                                        data={pieData}
                                        margin={{ top: 40, right: 20, bottom: 55, left: 20 }}
                                        innerRadius={0.85}
                                        padAngle={1}
                                        cornerRadius={8}
                                        activeOuterRadiusOffset={8}
                                        animate={true}
                                        startAngle={-150}
                                        endAngle={150}
                                        motionConfig="gentle"
                                        enableArcLabels={false}
                                        enableArcLinkLabels={true}
                                        arcLinkLabel={(d) => `${d.id} ${((d.value / deviceTotal) * 100).toFixed(1)}%`}
                                        arcLinkLabelsSkipAngle={10}
                                        arcLinkLabelsTextColor="#333"
                                        arcLinkLabelsThickness={2}
                                        arcLinkLabelsColor={{ from: 'color' }}
                                        arcLinkLabelsOffset={4}
                                        arcLinkLabelsDiagonalLength={16}
                                        arcLinkLabelsStraightLength={24}
                                        borderWidth={1}
                                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                        defs={defs}
                                        fill={fill}
                                        colors={({ id }) => {
                                            if (id === 'Desktop') return PRIMARY;
                                            if (id === 'Mobile') return SECONDARY;
                                            if (id === 'Tablet') return ACCENT;
                                            return '#cccccc'; // Colore di fallback
                                        }}
                                        enableSliceLabels={false}
                                        tooltip={({ datum }) => {
                                            const percentage = ((datum.value / deviceTotal) * 100).toFixed(1);
                                            return (
                                                <div style={tooltipStyle}>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <div style={{
                                                            width: '12px',
                                                            height: '12px',
                                                            backgroundColor: datum.color,
                                                            marginRight: '8px'
                                                        }} />
                                                        <span style={{ fontWeight: 'bold' }}>{datum.id}:</span>
                                                        <span style={{ marginLeft: '4px' }}>
                                                            {formatNumberBritish(datum.value, 0)} ({percentage}%)
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        }}
                                        legends={[
                                            {
                                                anchor: 'bottom',
                                                direction: 'row',
                                                translateY: 56,
                                                itemWidth: 80,
                                                itemHeight: 18,
                                                itemTextColor: '#333',
                                                symbolSize: 12,
                                                symbolShape: 'square',
                                                effects: [
                                                    {
                                                        on: 'hover',
                                                        style: {
                                                            itemTextColor: '#000'
                                                        }
                                                    }
                                                ]
                                            }
                                        ]}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        textAlign: 'center',
                                        pointerEvents: 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        gap: '0rem',
                                        height: '40%'
                                    }}>
                                        <div style={{ fontSize: '3rem', fontWeight: 'bold', lineHeight: '1' }}>
                                            {formatNumberBritish(deviceTotal)}
                                        </div>
                                        <div style={{ fontSize: '1rem', color: '#666', marginTop: '0.25rem' }}>
                                            TOTAL
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </>
                )}
            </Row>
        </>
    );
}

export default DeviceAnalytics;